<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { linkActions } from "@/action/linkActions";
import { selectionActions } from "@/action/selectionActions";
import { actionSourceKey } from "@/components/action/injectKeys";
import type { LinkableItem, LinkingTarget } from "@/model/link";
import { useBoardStore } from "@/store/board";
import { useCardStore } from "@/store/card";
import { linkId } from "@/store/helpers/link";
import { isObjective, isStickyLink } from "@/store/helpers/linkableItems";
import { useLinkStore } from "@/store/link";
import { useUserStore } from "@/store/user";
import { injectStrict } from "@/utils/vue";

import ListItemLink from "./internal/ListItemLink.vue";

const actionSource = injectStrict(actionSourceKey);

const { t } = useI18n();

const emit = defineEmits(["unlink"]);

const props = defineProps<{
  targets: LinkingTarget[];
  link: LinkableItem;
  showUpperLevelLinks?: boolean;
  showNestedLinks?: boolean;
}>();

const linkAnnouncement = ref("");

// Observers cannot remove links
const canEdit = computed(() => useUserStore().isAllowed("edit"));

function toggleLink(linkableItem: LinkableItem) {
  if (!canEdit.value) return;

  if (useBoardStore().areMultipleStickiesSelected) {
    selectionActions.link("modal", "selection");
  }

  if (linkableItem.linked) {
    linkableItem.linked = false;
    props.targets.forEach((target) => {
      emit("unlink");

      if (target.type === "objective" && isStickyLink(linkableItem)) {
        void linkActions.removeObjective(actionSource, linkableItem.id, target.boardId, target.id);
      } else if (target.type === "objective" && isObjective(linkableItem)) {
        void linkActions.removeObjectiveToObjective(
          actionSource,
          target.boardId,
          target.id,
          linkableItem.boardId,
          linkableItem.id,
        );
      }

      if (target.type === "sticky" && isObjective(linkableItem)) {
        void linkActions.removeObjective(
          actionSource,
          target.id,
          linkableItem.boardId,
          linkableItem.id,
        );
      } else if (target.type === "sticky" && isStickyLink(linkableItem)) {
        const linkId = getLinkId(target.id, linkableItem);
        if (!linkId) {
          throw Error(`Unable to find a link between: ${target.id} and ${linkableItem.id}`);
        }
        void linkActions.remove(actionSource, linkId, { from: target.id, to: linkableItem.id });
      }
    });
  } else {
    linkableItem.linked = true;
    props.targets.forEach((target) => {
      if (target.type === "objective" && isStickyLink(linkableItem)) {
        void linkActions.addObjective(actionSource, linkableItem.id, target.boardId, target.id);
      } else if (target.type === "objective" && isObjective(linkableItem)) {
        void linkActions.addObjectiveToObjective(
          actionSource,
          target.boardId,
          target.id,
          linkableItem.boardId,
          linkableItem.id,
        );
      }

      if (target.type === "sticky" && isObjective(linkableItem)) {
        void linkActions.addObjective(
          actionSource,
          target.id,
          linkableItem.boardId,
          linkableItem.id,
        );
      } else if (target.type === "sticky" && isStickyLink(linkableItem)) {
        void linkActions.add(actionSource, { fromId: target.id, toId: linkableItem.id });
      }
    });
  }

  linkAnnouncement.value = t("linkModal.linkAnnouncement", {
    kind: isObjective(linkableItem) ? t("linkModal.objective") : t("linkModal.sticky"),
    text: linkableItem.text,
    action: linkableItem.linked ? t("linkModal.linked") : t("linkModal.unlinked"),
  });

  setTimeout(() => {
    linkAnnouncement.value = "";
  }, 1000);
}

function getLinkId(fromId: string, to: LinkableItem) {
  const from = useCardStore().cards[fromId];
  const link = useLinkStore().links.find(
    (link) =>
      (link.from === linkId(from) || link.from === linkId(to)) &&
      (link.to === linkId(from) || link.to === linkId(to)),
  );
  return link?.id;
}
</script>

<template>
  <ListItemLink
    :link
    :link-announcement
    :can-edit
    :show-upper-level-links
    :show-nested-links
    @toggle-link="toggleLink"
  />
</template>
